import type { ResolvedPromoCountdown } from "../types";

export interface TimeParts {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    expired: boolean;
}

export type CountdownView =
    | { kind: "running"; parts: TimeParts }
    | { kind: "zero" }
    | { kind: "replace"; message: string | null; ctaText: string | null; ctaLink: string | null }
    | { kind: "hidden" };

/**
 * Split the time left until `targetMs` into units. Always computed from the
 * absolute target, so a throttled background tab catches up on the next tick.
 */
export function splitTime(targetMs: number, nowMs: number): TimeParts {
    const diff = targetMs - nowMs;
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };

    const totalSeconds = Math.floor(diff / 1000);
    return {
        days: Math.floor(totalSeconds / 86_400),
        hours: Math.floor((totalSeconds % 86_400) / 3600),
        minutes: Math.floor((totalSeconds % 3600) / 60),
        seconds: totalSeconds % 60,
        expired: false,
    };
}

/**
 * Apply render-time state to a (cached) countdown. Once the target has passed,
 * `hide` drops the countdown so the promo renders as if it never had one, and
 * the other behaviours render their ended state on the server — no placeholder
 * that later collapses or changes.
 */
export function settleCountdown(
    countdown: ResolvedPromoCountdown | null | undefined,
    nowMs: number
): ResolvedPromoCountdown | null {
    if (!countdown) return null;
    if (Date.parse(countdown.targetDate) > nowMs) return countdown;
    if (countdown.onExpire === "hide") return null;
    return { ...countdown, endedAtRender: true };
}

/** Decide what the countdown block shows for the current time. */
export function resolveCountdownView(countdown: ResolvedPromoCountdown, parts: TimeParts): CountdownView {
    if (!parts.expired) return { kind: "running", parts };

    switch (countdown.onExpire) {
        case "stayAtZero":
            return { kind: "zero" };
        case "replace":
            if (!countdown.expiredMessage && !countdown.expiredCtaText) return { kind: "hidden" };
            return {
                kind: "replace",
                message: countdown.expiredMessage,
                ctaText: countdown.expiredCtaText,
                ctaLink: countdown.expiredCtaLink,
            };
        case "hide":
            return { kind: "hidden" };
    }
}
